// Multi-pedigree-tabs — workspace reducer (pure).
//
// Owns the set of open pedigrees, their tab order, which is active, and which
// (single) one is the transient "preview". Pure and side-effect free: the caller
// supplies `sessionId` and `now` on the actions that create/activate sessions, so
// the reducer never touches Date.now()/random and is fully unit-testable and
// resume-safe.
//
// Session shape (one open pedigree instance):
//   { sessionId, familyId, title, mode: 'preview'|'pinned',
//     lock: 'none'|'self'|'other', lastActiveAt: number,
//     viewState: object|null }
//
// There is deliberately no "unsaved" flag. A pedigree has no save action — the
// chart autosaves and record saves live in the person panel — so a tab is never
// in an unsaved state. `lock` is the real per-tab status, and the tab strip
// reads it directly (TGCLOUD-1665).
//
// Invariants the reducer maintains:
//   - At most one session with mode 'preview' (previewSessionId points to it).
//   - One familyId is open at most once (opening an already-open family just
//     activates its existing tab — no duplicates).
//   - activeSessionId is always either null or a real session id.

export const WORKSPACE_ACTIONS = Object.freeze({
  OPEN: 'workspace/open',
  SET_ACTIVE: 'workspace/setActive',
  PIN: 'workspace/pin',
  MARK_EDITED: 'workspace/markEdited',
  SET_LOCK: 'workspace/setLock',
  UPDATE_VIEW_STATE: 'workspace/updateViewState',
  RENAME: 'workspace/rename',
  SET_PROBAND_NAME: 'workspace/setProbandName',
  REQUEST_RELEASE: 'workspace/requestRelease',
  MARK_INACCESSIBLE: 'workspace/markInaccessible',
  CLOSE: 'workspace/close',
  CLOSE_OTHERS: 'workspace/closeOthers',
  CLOSE_ALL: 'workspace/closeAll',
  REORDER: 'workspace/reorder',
  RESTORE: 'workspace/restore'
});

export const initialWorkspaceState = Object.freeze({
  sessions: [],
  activeSessionId: null,
  previewSessionId: null
});

const makeSession = ({ sessionId, familyId, title, mode, now }) => ({
  sessionId,
  familyId,
  title: title || familyId,
  // Proband display name, filled once the family loads (for the tab tooltip).
  probandName: '',
  mode,
  lock: 'none',
  inaccessible: false,
  // Bumped by a manual "Release lock" tab action; the instance watches it and
  // releases its edit lock (works for a background tab without switching to it).
  releaseNonce: 0,
  lastActiveAt: now || 0,
  viewState: null
});

const indexById = (sessions, sessionId) =>
  sessions.findIndex((s) => s.sessionId === sessionId);

const indexByFamily = (sessions, familyId) =>
  sessions.findIndex((s) => s.familyId === familyId);

// After a close, pick the next active session: prefer the left neighbour of the
// closed one, else the new right neighbour, else the preview, else null.
const pickNeighbour = (sessions, closedIndex) => {
  if (sessions.length === 0) return null;
  const leftward = sessions[closedIndex - 1];
  if (leftward) return leftward.sessionId;
  const rightward = sessions[closedIndex]; // element that shifted into the slot
  if (rightward) return rightward.sessionId;
  return sessions[sessions.length - 1].sessionId;
};

export function workspaceReducer(state = initialWorkspaceState, action) {
  switch (action?.type) {
    case WORKSPACE_ACTIONS.OPEN: {
      const {
        familyId,
        title,
        sessionId,
        now,
        activate = true,
        pinned = false
      } = action;
      if (!familyId || !sessionId) return state;

      // Already open (preview or pinned): activate it, refresh recency, never
      // duplicate. If it was a preview and we're opening as pinned, promote it.
      const existingIdx = indexByFamily(state.sessions, familyId);
      if (existingIdx !== -1) {
        const existing = state.sessions[existingIdx];
        const promoted =
          pinned && existing.mode === 'preview'
            ? { ...existing, mode: 'pinned' }
            : existing;
        const sessions = state.sessions.map((s, i) =>
          i === existingIdx
            ? { ...promoted, lastActiveAt: now ?? s.lastActiveAt }
            : s
        );
        return {
          ...state,
          sessions,
          activeSessionId: activate
            ? existing.sessionId
            : state.activeSessionId,
          previewSessionId:
            promoted.mode === 'pinned' &&
            state.previewSessionId === existing.sessionId
              ? null
              : state.previewSessionId
        };
      }

      const newSession = makeSession({
        sessionId,
        familyId,
        title,
        mode: pinned ? 'pinned' : 'preview',
        now
      });

      // Opening a NEW preview replaces the current preview IN PLACE (so the tab
      // doesn't jump); opening pinned (or when there's no preview) appends.
      if (!pinned && state.previewSessionId) {
        const previewIdx = indexById(state.sessions, state.previewSessionId);
        if (previewIdx !== -1) {
          const sessions = state.sessions.map((s, i) =>
            i === previewIdx ? newSession : s
          );
          return {
            ...state,
            sessions,
            activeSessionId: activate ? sessionId : state.activeSessionId,
            previewSessionId: sessionId
          };
        }
      }

      return {
        ...state,
        sessions: [...state.sessions, newSession],
        activeSessionId: activate ? sessionId : state.activeSessionId,
        previewSessionId: pinned ? state.previewSessionId : sessionId
      };
    }

    case WORKSPACE_ACTIONS.SET_ACTIVE: {
      const { sessionId, now } = action;
      if (indexById(state.sessions, sessionId) === -1) return state;
      return {
        ...state,
        activeSessionId: sessionId,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId
            ? { ...s, lastActiveAt: now ?? s.lastActiveAt }
            : s
        )
      };
    }

    case WORKSPACE_ACTIONS.PIN: {
      const { sessionId } = action;
      const idx = indexById(state.sessions, sessionId);
      if (idx === -1) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId ? { ...s, mode: 'pinned' } : s
        ),
        previewSessionId:
          state.previewSessionId === sessionId ? null : state.previewSessionId
      };
    }

    // The user edited the pedigree: auto-pin it, so a preview tab can't be
    // silently recycled out from under their working context. ONE effect, not
    // two — this used to also raise a `hasUnsavedChanges` flag that nothing ever
    // cleared, which drove a bogus close prompt for the rest of the session
    // (TGCLOUD-1665). Whether the tab holds the edit lock is tracked by SET_LOCK
    // and self-heals on release; "edited" needs no separate memory.
    case WORKSPACE_ACTIONS.MARK_EDITED: {
      const { sessionId } = action;
      const idx = indexById(state.sessions, sessionId);
      if (idx === -1) return state;
      // No-op when already pinned — a repeat edit must not churn a new state
      // object (and a new context value) on every keystroke-driven autosave.
      if (state.sessions[idx].mode === 'pinned') return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId ? { ...s, mode: 'pinned' } : s
        ),
        previewSessionId:
          state.previewSessionId === sessionId ? null : state.previewSessionId
      };
    }

    case WORKSPACE_ACTIONS.SET_LOCK: {
      const { sessionId, lock } = action;
      const idx = indexById(state.sessions, sessionId);
      if (idx === -1) return state;
      if (!['none', 'self', 'other'].includes(lock)) return state;
      // No-op when the lock badge is already this value — avoids churning a new
      // state object (and a new context value) on every badge re-sync.
      if (state.sessions[idx].lock === lock) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId ? { ...s, lock } : s
        )
      };
    }

    case WORKSPACE_ACTIONS.UPDATE_VIEW_STATE: {
      const { sessionId, patch } = action;
      if (indexById(state.sessions, sessionId) === -1) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.sessionId === sessionId
            ? { ...s, viewState: { ...(s.viewState || {}), ...(patch || {}) } }
            : s
        )
      };
    }

    case WORKSPACE_ACTIONS.RENAME: {
      const { familyId, title } = action;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.familyId === familyId ? { ...s, title } : s
        )
      };
    }

    case WORKSPACE_ACTIONS.SET_PROBAND_NAME: {
      const { familyId, probandName } = action;
      const idx = indexByFamily(state.sessions, familyId);
      if (idx === -1) return state;
      // No-op when unchanged — the source effect re-runs on every family load.
      if (state.sessions[idx].probandName === (probandName || '')) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.familyId === familyId ? { ...s, probandName: probandName || '' } : s
        )
      };
    }

    case WORKSPACE_ACTIONS.REQUEST_RELEASE: {
      const { familyId } = action;
      const idx = indexByFamily(state.sessions, familyId);
      if (idx === -1) return state;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.familyId === familyId
            ? { ...s, releaseNonce: (s.releaseNonce || 0) + 1 }
            : s
        )
      };
    }

    case WORKSPACE_ACTIONS.MARK_INACCESSIBLE: {
      const { familyId } = action;
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.familyId === familyId
            ? { ...s, inaccessible: true, lock: 'none' }
            : s
        )
      };
    }

    case WORKSPACE_ACTIONS.CLOSE: {
      const { sessionId } = action;
      const idx = indexById(state.sessions, sessionId);
      if (idx === -1) return state;
      const sessions = state.sessions.filter((s) => s.sessionId !== sessionId);
      const activeSessionId =
        state.activeSessionId === sessionId
          ? pickNeighbour(sessions, idx)
          : state.activeSessionId;
      return {
        ...state,
        sessions,
        activeSessionId,
        previewSessionId:
          state.previewSessionId === sessionId ? null : state.previewSessionId
      };
    }

    case WORKSPACE_ACTIONS.CLOSE_OTHERS: {
      const { sessionId } = action;
      const keep = state.sessions.find((s) => s.sessionId === sessionId);
      if (!keep) return state;
      return {
        ...state,
        sessions: [keep],
        activeSessionId: sessionId,
        previewSessionId:
          state.previewSessionId === sessionId ? sessionId : null
      };
    }

    case WORKSPACE_ACTIONS.CLOSE_ALL:
      return { ...initialWorkspaceState };

    case WORKSPACE_ACTIONS.REORDER: {
      const { order } = action;
      if (!Array.isArray(order)) return state;
      // Reorder by an explicit list of session ids; ignore ids that don't exist
      // and append any session the list forgot (defensive against stale input).
      const byId = new Map(state.sessions.map((s) => [s.sessionId, s]));
      const reordered = order.map((id) => byId.get(id)).filter(Boolean);
      const missing = state.sessions.filter(
        (s) => !order.includes(s.sessionId)
      );
      const sessions = [...reordered, ...missing];
      if (sessions.length !== state.sessions.length) return state;
      return { ...state, sessions };
    }

    case WORKSPACE_ACTIONS.RESTORE: {
      const { sessions, activeSessionId, previewSessionId } = action;
      if (!Array.isArray(sessions)) return state;
      const ids = new Set(sessions.map((s) => s.sessionId));
      return {
        sessions,
        activeSessionId: ids.has(activeSessionId) ? activeSessionId : null,
        previewSessionId: ids.has(previewSessionId) ? previewSessionId : null
      };
    }

    default:
      return state;
  }
}

// ---- Action creators (keep the impure bits — id/timestamp — at the edge) ----

export const openPedigreeAction = ({
  familyId,
  title,
  sessionId,
  now,
  activate = true,
  pinned = false
}) => ({
  type: WORKSPACE_ACTIONS.OPEN,
  familyId,
  title,
  sessionId,
  now,
  activate,
  pinned
});

export const setActiveAction = (sessionId, now) => ({
  type: WORKSPACE_ACTIONS.SET_ACTIVE,
  sessionId,
  now
});

export const pinSessionAction = (sessionId) => ({
  type: WORKSPACE_ACTIONS.PIN,
  sessionId
});

export const markEditedAction = (sessionId) => ({
  type: WORKSPACE_ACTIONS.MARK_EDITED,
  sessionId
});

export const setLockAction = (sessionId, lock) => ({
  type: WORKSPACE_ACTIONS.SET_LOCK,
  sessionId,
  lock
});

export const updateViewStateAction = (sessionId, patch) => ({
  type: WORKSPACE_ACTIONS.UPDATE_VIEW_STATE,
  sessionId,
  patch
});

export const renameSessionAction = (familyId, title) => ({
  type: WORKSPACE_ACTIONS.RENAME,
  familyId,
  title
});

export const setProbandNameAction = (familyId, probandName) => ({
  type: WORKSPACE_ACTIONS.SET_PROBAND_NAME,
  familyId,
  probandName
});

export const requestReleaseAction = (familyId) => ({
  type: WORKSPACE_ACTIONS.REQUEST_RELEASE,
  familyId
});

export const markInaccessibleAction = (familyId) => ({
  type: WORKSPACE_ACTIONS.MARK_INACCESSIBLE,
  familyId
});

export const closeSessionAction = (sessionId) => ({
  type: WORKSPACE_ACTIONS.CLOSE,
  sessionId
});

export const closeOthersAction = (sessionId) => ({
  type: WORKSPACE_ACTIONS.CLOSE_OTHERS,
  sessionId
});

export const closeAllAction = () => ({ type: WORKSPACE_ACTIONS.CLOSE_ALL });

export const reorderTabsAction = (order) => ({
  type: WORKSPACE_ACTIONS.REORDER,
  order
});

export const restoreWorkspaceAction = ({
  sessions,
  activeSessionId,
  previewSessionId
}) => ({
  type: WORKSPACE_ACTIONS.RESTORE,
  sessions,
  activeSessionId,
  previewSessionId
});

// ---- Selectors ----

export const getActiveSession = (state) =>
  state.sessions.find((s) => s.sessionId === state.activeSessionId) || null;

export const getSessionByFamily = (state, familyId) =>
  state.sessions.find((s) => s.familyId === familyId) || null;

export const isFamilyOpen = (state, familyId) =>
  state.sessions.some((s) => s.familyId === familyId);
